import { useContext, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { isNeedChangeGanttDate, useGetGanttDataQuery } from '@entities/gantt';
import { getEstimateInformationQuery } from '@widgets/EstimateInformation/api/hooks/getEstimateInformationQuery';
import { useUpdateEstimateStatusQuery } from '@widgets/EstimateInformation/api/hooks/useUpdateEstimateStatusQuery';
import { EstimateInformationContext } from '@widgets/EstimateInformation/model/context';
import { Status } from '@widgets/EstimateInformation/model/types/types';

export const useEstimateStatus = () => {
    const { t } = useTranslation();
    const { refetch: refetchGantt } = useGetGanttDataQuery();
    const { status, onSetStatus, onOpenGanttModal } = useContext(EstimateInformationContext);

    const updateEstimateStatusMutation = useUpdateEstimateStatusQuery();
    const [isShowPopup, setIsShowPopup] = useState<Status.DRAFT | Status.IN_EXPLOITATIONS | null>(
        null
    );
    const { data, refetch, isPending } = getEstimateInformationQuery();
    const statusMapping = {
        [Status.DRAFT]: t('useEstimateStatus.statusMapping.DRAFT'),
        [Status.IN_EXPLOITATIONS]: t('useEstimateStatus.statusMapping.IN_EXPLOITATIONS'),
        [Status.ON_AGREEMENT]: t('useEstimateStatus.statusMapping.ON_AGREEMENT'),
        [Status.COMPLETED]: t('useEstimateStatus.statusMapping.COMPLETED'),
    };

    const btnRef = useRef<HTMLDivElement | null>(null);

    const [isShowDropdown, setIsShowDropdown] = useState(false);

    const onShowDropdown = () => {
        if (isShowDropdown) {
            setIsShowDropdown(false);
        } else {
            setIsShowDropdown(true);
        }
    };

    const onHideDropdown = () => {
        setIsShowDropdown(false);
    };

    const onChangeStatus = (value: Status) => async () => {
        const isLockedByTariff =
            data?.status.workAndCustomerApprovalLockedByTariff &&
            value === Status.IN_EXPLOITATIONS;

        if (isLockedByTariff) {
            onHideDropdown();
            await updateEstimateStatusMutation.mutateAsync(value);
            return;
        }

        if (status === Status.DRAFT && data?.canViewSchedule) {
            const ganttData = await refetchGantt();
            const isNeedShowModalGantt = isNeedChangeGanttDate(ganttData.data.data);

            if (isNeedShowModalGantt) {
                onOpenGanttModal();
                return;
            }
        }

        if (status === Status.IN_EXPLOITATIONS && value === Status.DRAFT) {
            setIsShowPopup(Status.IN_EXPLOITATIONS);
            return;
        }
        const response = await updateEstimateStatusMutation.mutateAsync(value);
        if (response?.response?.data?.status === 'tariff-error') {
            return;
        }
        onSetStatus(value);
        await refetch();
        onHideDropdown();
        window.location.reload();
    };

    const onSetDraft = async () => {
        setIsShowPopup(null);
        onSetStatus(Status.DRAFT);
        await updateEstimateStatusMutation.mutateAsync(Status.DRAFT);
        await refetch();
        window.location.reload();
    };

    const onHidePopup = () => {
        setIsShowPopup(null);
    };
    return {
        status,
        statusMapping,
        btnRef,
        onShowDropdown,
        onHideDropdown,
        onChangeStatus,
        isShowDropdown,
        data,
        isShowPopup,
        t,
        onHidePopup,
        onSetDraft,
        isPending,
    };
};
